import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import RecapCard from './RecapCard';
import { Roles } from 'utils/constants';
import { useHistory } from 'react-router-dom';
import { SdmCheckboxV5 } from 'components/SdmCheckboxV5';
import MerchCard from './MerchCard';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useState } from 'react';
import AddDialog from 'pages/ticketV2/common/AddDialog';
import {
  IS_MERCH_COUNT_IN_TOT,
  ScrollableSections,
} from 'pages/ticketV2/common';

const RecapContainer = ({
  loggedUser,
  conditionsOfSales,
  setConditionsOfSales,
  locale,
  recapMessageTexts,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    ticketsData,
    productsInCart,
    loggedDataAtIndex,
    merchInCart,
    fetchedProducts,
    isCartFull,
    submittedDiscountCodesWithEmptySpaces,
  } = useTicketsStore();
  const history = useHistory();
  const intl = useIntl();
  const i18nRecap = translations.TicketsV2(intl).Recap;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isOperator = loggedUser.type === Roles.OPERATOR;

  const handleEdit = (nextStepperIdx) => {
    useTicketsStore.setState({
      stepperCurrentTicketIdx: nextStepperIdx,
      isEditingTicketMode: true,
    });
    history.push('/assign-ticket');
  };

  const handleAddCatalogue = () => {
    useTicketsStore.setState({
      isAddingCatalogueMode: true,
    });
    history.push('/merch');
  };

  const handleDelete = (idxToDelete, ticket) => {
    const nextTicketsData = [...ticketsData];
    nextTicketsData.splice(idxToDelete, 1);
    submittedDiscountCodesWithEmptySpaces?.splice(idxToDelete, 1);

    const nextProdInCart = [...productsInCart];
    const prodInCartIdx = nextProdInCart.findIndex(
      (prod) => prod.id === ticket.id
    );
    const targetProd = nextProdInCart[prodInCartIdx];
    nextProdInCart[prodInCartIdx] = {
      ...targetProd,
      quantity: targetProd.quantity - 1,
    };

    const hasTicket = nextTicketsData.length;

    let nextLoggedDataAtIndex = loggedDataAtIndex;
    if (idxToDelete < loggedDataAtIndex)
      nextLoggedDataAtIndex = loggedDataAtIndex - 1;
    if (idxToDelete === loggedDataAtIndex) nextLoggedDataAtIndex = null;

    useTicketsStore.setState({
      productsInCart: nextProdInCart,
      ticketsData: nextTicketsData,
      stepperCurrentTicketIdx: hasTicket ? nextTicketsData.length - 1 : 0,
      loggedDataAtIndex: nextLoggedDataAtIndex,
    });

    if (!hasTicket) history.push('/');
  };

  const closeDialog = () => setIsDialogOpen(false);

  const handleAddMoreTickets = (ticketsQuantity) => {
    const _newCart = fetchedProducts.map((pro) => ({
      ...pro,
      quantity: ticketsQuantity[pro.id],
    }));
    const updatedCart = _newCart.map((newTicket) => {
      const found = productsInCart.find((item) => newTicket.id === item.id);
      if (found) {
        return { ...found, quantity: found.quantity + newTicket.quantity };
      } else {
        return newTicket;
      }
    });

    useTicketsStore.setState({
      stepperCurrentTicketIdx: ticketsData.length,
      productsInCart: updatedCart,
    });
    history.push(`/assign-ticket`);
    closeDialog();
  };

  const hasValidMerch =
    merchInCart.filter((merch) => merch && merch.quantity && merch.quantity > 0)
      ?.length > 0;

  return (
    <Box
      sx={{
        width: isLgDown ? '100%' : '64%',
        ...(!isLgDown && {
          pr: '70px',
        }),
      }}
    >
      <Typography variant="xl" sx={{ mb: '10px' }} component={'h1'}>
        {recapMessageTexts.recapTitle}
      </Typography>
      <Stack sx={{ mt: '25px' }} direction={'column'} gap={'25px'}>
        {ticketsData?.map((td, idx) => (
          <RecapCard
            key={idx}
            idx={idx}
            onEdit={() => handleEdit(idx)}
            onDelete={() => handleDelete(idx, td.ticketData)}
            product={td.ticketData}
            assignData={td.assignData}
            isSmDown={isSmDown}
            isMdDown={isMdDown}
            isOperator={isOperator}
          />
        ))}
        {hasValidMerch
          ? merchInCart
              .filter((merch) => merch && merch.quantity && merch.quantity > 0)
              .map((merch) => (
                <MerchCard
                  key={merch.id}
                  merch={merch}
                  isSmDown={isSmDown}
                  isMdDown={isMdDown}
                  isOperator={isOperator}
                  locale={locale}
                  loggedUser={loggedUser}
                />
              ))
          : null}
      </Stack>
      <Stack
        flexDirection={isMdDown ? 'column' : 'row'}
        justifyContent={!hasValidMerch ? 'space-between' : 'flex-end'}
      >
        {!hasValidMerch &&
        (!isCartFull || (isCartFull && !IS_MERCH_COUNT_IN_TOT)) ? (
          <Button
            variant={'outlined'}
            color={'primary'}
            sx={{ mt: '25px' }}
            onClick={() => handleAddCatalogue()}
          >
            {i18nRecap.addCatalogue}
          </Button>
        ) : null}
        {!isCartFull ? (
          <Button
            variant={'outlined'}
            color={'primary'}
            sx={{
              mt: '25px',
              ...(isMdDown && {
                mb: '25px',
              }),
            }}
            onClick={() => setIsDialogOpen(true)}
          >
            {i18nRecap.moreTickets}
          </Button>
        ) : null}
      </Stack>
      <Typography
        variant="xl"
        component={'h2'}
        sx={{
          mb: '20px',
          ...(!isSmDown && {
            mt: '25px',
          }),
        }}
      >
        {recapMessageTexts.conditionsOfSale}
      </Typography>
      <ScrollableSections style={{ marginBottom: '17px' }}>
        <Typography variant="link2" paragraph>
          {recapMessageTexts.conditionsOfSaleParsedBody}
        </Typography>
      </ScrollableSections>
      <div>
        <SdmCheckboxV5
          onChange={(e) => {
            setConditionsOfSales((prev) => ({
              ...prev,
              one: e.target.checked,
            }));
          }}
          variant="black"
          value={conditionsOfSales.one}
          isSelected={conditionsOfSales.one}
          label={recapMessageTexts.accept}
          labelPlacement={'end'}
          customFont={'text'}
          style={{ marginLeft: 15 }}
        />
      </div>
      <Typography
        variant="link2"
        paragraph
        sx={{ mb: '10px', fontStyle: 'italic' }}
      >
        {recapMessageTexts.secondAccept}
      </Typography>
      <div>
        <SdmCheckboxV5
          onChange={(e) => {
            setConditionsOfSales((prev) => ({
              ...prev,
              two: e.target.checked,
            }));
          }}
          variant="black"
          value={conditionsOfSales.twp}
          isSelected={conditionsOfSales.two}
          label={recapMessageTexts.accept}
          labelPlacement={'end'}
          customFont={'text'}
          style={{ marginLeft: 15 }}
        />
      </div>
      <AddDialog
        isOpen={isDialogOpen}
        onClose={() => closeDialog()}
        onAccept={handleAddMoreTickets}
        locale={locale}
        loggedUser={loggedUser}
      />
    </Box>
  );
};
export default RecapContainer;
